import {
  useFeatureFlags,
  useFeatureFlagsProviderContext,
} from '@brightdrop/feature-flags-client';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { EnablementsDto } from '../api/entitlementsApi';
import {
  PLAN_ENABLEMENT_FLAG_NAMES,
  PLAN_ENABLEMENT_FLAG_NAMES_LIST,
} from '../config/planEnablement.config';
import type { EntitlementsState } from '../state/entitlementsSlice';
import {
  isFeatureEntitledFromEnablementsData,
  isFeatureEntitledFromOrgProductEntitlements,
  isProductEntitledFromEnablementsData,
  isProductEntitledFromOrgProductEntitlements,
} from '../utils/entitlements.utils';
import {
  getMergedPlanEnablement,
  isFeatureInMarketplaceEnablement,
  isMergedPlanProductFeatureEnabled,
  resolveMarketplaceAndPlanManagementAccess,
} from './enablement.helpers';

/** Organization-level entitlements / enablements (same source as {@link useEntitlements} without asset id). */
export type PlanMarketplaceAccessOrganizationScope = {
  scope: 'organization';
};

/** Per-asset enablements (vehicle or peripheral), aligned with {@link useEntitlements} asset arguments. */
export type PlanMarketplaceAccessAssetScope = {
  scope: 'asset';
  assetId: string;
  assetType?: 'vehicle' | 'peripheral';
};

export type UsePlanMarketplaceAccessOptions = (
  | PlanMarketplaceAccessOrganizationScope
  | PlanMarketplaceAccessAssetScope
) & {
  productCode: string;
  featureCode: string;
  /**
   * LD keys merged for plan enablement (default: {@link PLAN_ENABLEMENT_FLAG_NAMES_LIST}).
   */
  planEnablementFlagNames?: string[];
  /**
   * Marketplace catalog flag key (default: {@link PLAN_ENABLEMENT_FLAG_NAMES.BPS_MARKETPLACE}).
   */
  marketplaceEnablementFlagName?: string;

  /**
   * Per-asset enablements payload (VIN/peripheral) used for asset-scope entitlement checks.
   *
   * Rationale: organization-level base entitlements originally exposed VIN enablements, but that
   * source is capped to the first 100 records. For assets beyond that window, UI gating can become
   * inaccurate. Supplying enablements resolved by the specific `assetId` ensures product/feature
   * visibility is validated against the correct asset.
   */
  assetEnablements?: EnablementsDto;
};

/**
 * Result of {@link usePlanMarketplaceAccess}.
 *
 * **Glossary (how fields relate):**
 * - **`isMarketplaceCatalogAllowsPair`** — raw marketplace LD value only (object catalog or legacy
 *   product array); not the same as the `allow` field from {@link resolveMarketplaceAndPlanManagementAccess}.
 * - **`allowResolvedMarketplaceOrPlanManagement`** — {@link resolveMarketplaceAndPlanManagementAccess};
 *   the hook also forces `false` when `!isUserContextIdentified`.
 * - **`showFeature` / `showMixedPlanMessage` / `canManageSubscription`** — combine merged plan map,
 *   marketplace catalog, entitlement-from-data, resolved allow, and user-context rules in this hook.
 */
export type UsePlanMarketplaceAccessResult = {
  /**
   * License / enablements payload only (no plan LD gating), for mixed-plan copy and comparison with plan-gated fields.
   */
  isEntitledFromData: boolean;
  /**
   * Merged plan enablement includes this product/feature pair, the product has at least one
   * plan-enabled feature (empty `[]` disables the product), and plan LD data is non-empty.
   * Same predicate as {@link isMergedPlanProductFeatureEnabled} for this hook’s merged map.
   */
  isPlanFeatureEnabled: boolean;
  /** Raw marketplace flag allows this pair (catalog or legacy array shape). */
  isMarketplaceCatalogAllowsPair: boolean;
  /**
   * Both merged plan and marketplace catalog allow the pair; empty merged plan ⇒ false.
   * When `isUserContextIdentified` is false, the hook returns `false` here regardless.
   */
  allowResolvedMarketplaceOrPlanManagement: boolean;
  /** Surface feature nav / content when plan allows the pair and user is entitled or marketplace enables purchase path. */
  showFeature: boolean;
  /** Mixed-plan UX: not entitled from data but plan and marketplace catalog allow the pair. */
  showMixedPlanMessage: boolean;
  /** Manage subscription in BPS when entitled from data and `allowResolvedMarketplaceOrPlanManagement` is true. */
  canManageSubscription: boolean;
  /**
   * `isEntitledFromData` **and** merged plan allows this pair (`isPlanFeatureEnabled`). Empty **P** ⇒ false.
   */
  hasEffectiveFeatureAccess: boolean;
  /**
   * From `useFeatureFlagsProviderContext` — `false` while the **latest** LD `identify` from
   * `setContext` has not completed (including re-identify after context changes). When `false`,
   * marketplace / mixed-plan / subscription surfaces use conservative defaults.
   */
  isUserContextIdentified: boolean;
};

/**
 * Whether license / enablements data lists the product or feature (no plan flags): delegates org vs asset
 * reads to {@link isFeatureEntitledFromOrgProductEntitlements}, {@link isProductEntitledFromOrgProductEntitlements},
 * {@link isFeatureEntitledFromEnablementsData}, and {@link isProductEntitledFromEnablementsData} in
 * `entitlements.utils`.
 */
function computeIsEntitledFromData(
  entitlements: EntitlementsState | undefined,
  assetEnablements: EnablementsDto | undefined,
  productCode: string,
  featureCode: string,
  scope: UsePlanMarketplaceAccessOptions['scope'],
  assetId: string | undefined,
  assetType: 'vehicle' | 'peripheral' | undefined
): boolean {
  if (!entitlements?.rawEntitlements) {
    return false;
  }
  const orgEntitlements = entitlements.rawEntitlements.entitlements;

  if (scope === 'organization') {
    return (
      isFeatureEntitledFromOrgProductEntitlements(
        featureCode,
        orgEntitlements
      ) ||
      isProductEntitledFromOrgProductEntitlements(productCode, orgEntitlements)
    );
  }

  if (!assetId || !assetEnablements) {
    return false;
  }
  const resolvedAssetType = assetType ?? 'vehicle';
  if (resolvedAssetType === 'vehicle' || resolvedAssetType === 'peripheral') {
    return (
      isFeatureEntitledFromEnablementsData(
        featureCode,
        assetId,
        assetEnablements,
        resolvedAssetType
      ) ||
      isProductEntitledFromEnablementsData(
        productCode,
        assetId,
        assetEnablements,
        resolvedAssetType
      )
    );
  }
  return false;
}

/**
 * Combines {@link useFeatureFlags}, {@link useEntitlements}, merged plan enablement, and
 * {@link resolveMarketplaceAndPlanManagementAccess} for marketplace and plan-management UX.
 * Use **`scope: 'organization'`** for org catalog flows; **`scope: 'asset'`** with **`assetId`** for per-VIN/peripheral enablements.
 *
 * Note for asset scope: base organization entitlements can include VIN enablements, but only for
 * the first 100 records. Pass `assetEnablements` from an asset-id-driven enablements query so
 * entitlement checks reflect the requested asset rather than a truncated org-level set.
 *
 * @param options - Scope, product/feature codes, optional flag name overrides, and optional asset-level enablements data
 */
export function usePlanMarketplaceAccess(
  options: UsePlanMarketplaceAccessOptions
): UsePlanMarketplaceAccessResult {
  const { getFlag } = useFeatureFlags();
  const { isUserContextIdentified } = useFeatureFlagsProviderContext();
  const entitlements = useSelector(
    (state: { entitlements: EntitlementsState }) => state.entitlements
  );

  const planEnablementFlagNames =
    options.planEnablementFlagNames ?? PLAN_ENABLEMENT_FLAG_NAMES_LIST;
  const marketplaceEnablementFlagName =
    options.marketplaceEnablementFlagName ??
    PLAN_ENABLEMENT_FLAG_NAMES.BPS_MARKETPLACE;
  const assetEnablements = options?.assetEnablements;

  const { productCode, featureCode } = options;

  const mergedPlanEnablement = useMemo(
    () => getMergedPlanEnablement(getFlag, planEnablementFlagNames),
    [getFlag, planEnablementFlagNames]
  );

  const scope = options.scope;
  const assetId = options.scope === 'asset' ? options.assetId : undefined;
  const assetType = options.scope === 'asset' ? options.assetType : undefined;

  return useMemo((): UsePlanMarketplaceAccessResult => {
    const marketplaceFlagValue = getFlag(marketplaceEnablementFlagName);
    const isPlanFeatureEnabled = isMergedPlanProductFeatureEnabled(
      mergedPlanEnablement,
      productCode,
      featureCode
    );

    const isMarketplaceCatalogAllowsPair = isFeatureInMarketplaceEnablement(
      marketplaceFlagValue,
      productCode,
      featureCode
    );

    const { allow: allowResolvedMarketplaceOrPlanManagement } =
      resolveMarketplaceAndPlanManagementAccess({
        mergedPlanEnablement,
        marketplaceFlagValue,
        productCode,
        featureCode,
      });

    const isEntitledFromData = computeIsEntitledFromData(
      entitlements,
      assetEnablements,
      productCode,
      featureCode,
      scope,
      assetId,
      assetType
    );

    const hasEffectiveFeatureAccess =
      isEntitledFromData && isPlanFeatureEnabled;

    const showFeatureBase =
      isPlanFeatureEnabled &&
      (isEntitledFromData || isMarketplaceCatalogAllowsPair);

    const showMixedPlanMessageBase =
      !isEntitledFromData &&
      isPlanFeatureEnabled &&
      isMarketplaceCatalogAllowsPair;

    const canManageSubscriptionBase =
      isEntitledFromData && allowResolvedMarketplaceOrPlanManagement;

    const showFeature = isUserContextIdentified
      ? showFeatureBase
      : isPlanFeatureEnabled && isEntitledFromData;

    const showMixedPlanMessage = isUserContextIdentified
      ? showMixedPlanMessageBase
      : false;

    const allowResolvedMarketplaceOrPlanManagementGated =
      isUserContextIdentified
        ? allowResolvedMarketplaceOrPlanManagement
        : false;

    const canManageSubscription = isUserContextIdentified
      ? canManageSubscriptionBase
      : false;

    return {
      isEntitledFromData,
      isPlanFeatureEnabled,
      isMarketplaceCatalogAllowsPair,
      allowResolvedMarketplaceOrPlanManagement:
        allowResolvedMarketplaceOrPlanManagementGated,
      showFeature,
      showMixedPlanMessage,
      canManageSubscription,
      hasEffectiveFeatureAccess,
      isUserContextIdentified,
    };
  }, [
    assetEnablements,
    assetId,
    assetType,
    entitlements,
    featureCode,
    getFlag,
    isUserContextIdentified,
    marketplaceEnablementFlagName,
    mergedPlanEnablement,
    productCode,
    scope,
  ]);
}
